import { useEffect, useMemo } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { API } from "@/lib/api";
import {
  ShieldCheck, ArrowRight, CheckCircle, FileArrowDown, LinkSimple,
  Sparkle, TreeStructure, Warning, Lock, ArrowLeft,
} from "@phosphor-icons/react";

/**
 * Data-driven, per-ERP SEO landing pages.
 * Every vendor gets a dedicated URL: /integrations/:vendor
 * Meta title + description are set client-side (modern Googlebot renders JS).
 */

const VENDORS = {
  infor: {
    name: "Infor",
    products: "SunSystems · M3 · TG4 · ION",
    color: "#D50037",
    audience: "Irish public sector, healthcare boards, HSE partners, semi-state agencies",
    tagline: "Push PerCen360 compliance data straight into your Infor stack.",
    hero: "The compliance layer for Infor-run public sector organisations in Ireland.",
    bullets: [
      "Journal-shaped CSV with account codes, VAT codes (S1/Z0) and cost-centre references",
      "Native to Infor SunSystems, M3 and the TG4 configuration used across Irish public bodies",
      "Every export is audit-hashed for GDPR Article 30 record-of-processing",
      "Optional ION API live-sync coming — join the priority list from your dashboard",
    ],
    sample: "infor",
    columns: ["JournalDate", "AccountCode", "Description", "DebitAmount", "CreditAmount", "Currency", "VATCode", "Reference"],
    faqs: [
      { q: "Does this work with the Infor TG4 configuration?", a: "Yes. The account code and VAT code columns match the TG4 chart-of-accounts convention. Your finance team can extend the mapping in Settings." },
      { q: "Can I connect live via ION API?", a: "Live sync via Infor ION API is on the roadmap. In the meantime, use the CSV — it drops directly into SunSystems and M3." },
      { q: "Is this suitable for HSE / semi-state audits?", a: "Yes. Every export carries an audit hash and is logged for 7 years per Irish public sector retention requirements." },
    ],
    seoTitle: "PerCen360 for Infor — Irish public sector compliance",
    seoDesc: "Export grant and receipt data from PerCen360 directly to Infor SunSystems, M3 and TG4. GDPR-compliant, audit-hashed, EU region.",
  },
  flowforma: {
    name: "FlowForma",
    products: "Power Automate · SharePoint · HSE workflows",
    color: "#00B67A",
    audience: "HSE, TG4, public sector process teams",
    tagline: "Ship compliance events into your FlowForma processes automatically.",
    hero: "Grant compliance as a first-class step in your FlowForma flows.",
    bullets: [
      "SharePoint list-shaped CSV: Title, ExpenseCategory, TransactionDate, Amount, VAT, ComplianceStatus",
      "Trigger Power Automate flows on every receipt via HMAC-signed webhook",
      "Compliance status field pre-populated (PASS/WARN/FAIL) so your approvers can filter",
      "Native to the Irish public sector process stack",
    ],
    sample: "flowforma",
    columns: ["Title", "ExpenseCategory", "TransactionDate", "Amount", "VAT", "ComplianceStatus", "Notes", "SourceId"],
    faqs: [
      { q: "Can this trigger a FlowForma process automatically?", a: "Yes — subscribe a FlowForma webhook URL to receipt.created events. HMAC-SHA256 signature is included so you can verify the payload." },
      { q: "Does it work with SharePoint Online?", a: "The CSV drops directly into a SharePoint list. Column names match FlowForma list conventions." },
    ],
    seoTitle: "PerCen360 for FlowForma — HSE & public sector",
    seoDesc: "Push grant compliance data into FlowForma Power Automate flows. SharePoint-list ready CSV + HMAC-signed webhooks. GDPR-compliant.",
  },
  sap: {
    name: "SAP",
    products: "SAP Concur · SAP Ariba · S/4HANA",
    color: "#0FAAFF",
    audience: "Large enterprises, universities, multinational R&D groups",
    tagline: "Grant-body compliance data, drop-in ready for SAP Concur & S/4HANA.",
    hero: "The compliance last-mile for SAP-run Irish and EU organisations.",
    bullets: [
      "SAP Concur Standard Expense import — Employee ID, Expense Type, Transaction fields",
      "Journal-ready columns for S/4HANA GL posting",
      "EUR currency, Revenue.ie VAT rules pre-applied",
      "One-click compliance certificate to attach to expense approvals",
    ],
    sample: "sap-concur",
    columns: ["Employee ID", "Expense Type", "Transaction Date", "Transaction Amount", "Currency", "Vendor Name", "Payment Type", "Comment"],
    faqs: [
      { q: "Does this work with SAP Concur cloud?", a: "Yes. The CSV format matches Concur's Standard Expense import template. Your Concur admin can drop it directly into their import tool." },
      { q: "What about SAP Ariba supplier invoices?", a: "For Ariba, use the same CSV — mapping guidance is provided when you request the connector from your Integrations dashboard." },
    ],
    seoTitle: "PerCen360 for SAP — Concur, Ariba, S/4HANA",
    seoDesc: "Direct expense and grant compliance export from PerCen360 to SAP Concur, SAP Ariba and S/4HANA. Irish Revenue.ie VAT rules, GDPR audit-logged.",
  },
  netsuite: {
    name: "Oracle NetSuite",
    products: "NetSuite · Oracle Cloud ERP",
    color: "#F80000",
    audience: "Universities, corporates, higher education R&D",
    tagline: "Grant expense data, journal-ready for NetSuite.",
    hero: "The compliance feed for NetSuite-run universities and corporates.",
    bullets: [
      "NetSuite Journal Entry import: TranDate, Account, Memo, Debit, Credit, Currency, Class",
      "Grant projects map to Classes; expense categories to GL accounts",
      "Compatible with Oracle Cloud ERP journal imports",
    ],
    sample: "netsuite",
    columns: ["TranDate", "Account", "Memo", "Debit", "Credit", "Currency", "Class", "Reference"],
    faqs: [
      { q: "Which NetSuite version is supported?", a: "The CSV shape works with NetSuite OneWorld, mid-market and Oracle Cloud ERP." },
      { q: "Can I map grants to NetSuite Classes?", a: "Yes — every export line includes a Class column pre-set to your grant name." },
    ],
    seoTitle: "PerCen360 for Oracle NetSuite — universities & corporates",
    seoDesc: "Journal-ready CSV export from PerCen360 to Oracle NetSuite and Oracle Cloud ERP. Grant projects, GL accounts, Revenue.ie-compliant VAT.",
  },
  workday: {
    name: "Workday",
    products: "Workday HCM · Financials",
    color: "#F38B00",
    audience: "Universities, large orgs, growing Irish enterprises",
    tagline: "Employee-shaped expense reports for Workday.",
    hero: "Grant expenses posted to Workday, employee-shaped and business-purpose ready.",
    bullets: [
      "Workday Expense Report import format: Employee_ID, Expense_Item, Amount, Merchant",
      "Business_Purpose column pre-filled from grant name",
      "EUR currency, category taxonomy aligned to Workday spend categories",
    ],
    sample: "workday",
    columns: ["Employee_ID", "Expense_Date", "Expense_Item", "Amount", "Currency", "Merchant", "Description", "Business_Purpose"],
    faqs: [
      { q: "Does this work with Workday Financials expense modules?", a: "Yes. The CSV maps to the standard Workday Expense Report loader." },
    ],
    seoTitle: "PerCen360 for Workday — university & enterprise expenses",
    seoDesc: "Workday Expense Report import CSV from PerCen360 — Irish grant compliance, GDPR-native, EU region.",
  },
  sage: {
    name: "Sage",
    products: "Sage 50 · Sage 200 · Sage Business Cloud",
    color: "#00DC00",
    audience: "SMEs, spin-outs, growing Irish start-ups",
    tagline: "Sage accounting import — net, VAT, gross amounts aligned.",
    hero: "Grant and receipt data in Sage-ready shape.",
    bullets: [
      "Sage import CSV: Date, Reference, Details, NetAmount, TaxAmount, GrossAmount, NominalCode, TaxCode",
      "T1/T0 tax codes matching Sage Ireland conventions",
      "Perfect for Enterprise Ireland-funded SMEs and spin-outs",
    ],
    sample: "sage",
    columns: ["Date", "Reference", "Details", "NetAmount", "TaxAmount", "GrossAmount", "NominalCode", "TaxCode"],
    faqs: [
      { q: "Does this work with Sage 50 (Irish version)?", a: "Yes. Tax codes T1 (standard 23%) and T0 (zero-rate) match the Sage Ireland install." },
    ],
    seoTitle: "PerCen360 for Sage — Irish SMEs & spin-outs",
    seoDesc: "Sage 50 / 200 / Business Cloud import CSV from PerCen360 with Irish tax codes. GDPR-compliant grant tracking.",
  },
  xero: {
    name: "Xero",
    products: "Xero Bills · Bank feeds · API",
    color: "#13B5EA",
    audience: "Irish start-ups & health-tech spin-outs",
    tagline: "Xero-native bill import, ready to reconcile.",
    hero: "The go-to compliance backbone for Xero-running Irish start-ups.",
    bullets: [
      "Xero Bill CSV: *ContactName, *InvoiceDate, *DueDate, Description, UnitAmount, AccountCode, TaxType",
      "OUTPUT2 / EXEMPTOUTPUT tax types pre-set",
      "Tracking categories linked to your grant name so P&L splits are automatic",
    ],
    sample: "xero",
    columns: ["*ContactName", "*InvoiceDate", "*DueDate", "Description", "Quantity", "UnitAmount", "AccountCode", "TaxType"],
    faqs: [
      { q: "Can I connect to Xero via API instead of CSV?", a: "Live Xero API sync is on the paid roadmap. Join the priority list from your Integrations dashboard." },
    ],
    seoTitle: "PerCen360 for Xero — Irish start-ups",
    seoDesc: "Direct Bill import from PerCen360 to Xero with Irish tax types and tracking categories. Perfect for spin-outs and Enterprise Ireland recipients.",
  },
  quickbooks: {
    name: "QuickBooks",
    products: "QuickBooks Online · Desktop",
    color: "#2CA01C",
    audience: "SMEs, founders, sole traders",
    tagline: "Three-column bank CSV, ready to import.",
    hero: "The simplest possible route from PerCen360 to QuickBooks.",
    bullets: [
      "Standard 3-column QB bank CSV: Date, Description, Amount",
      "Money-out lines pre-negated for cash-flow accuracy",
      "Drop into QB Online 'Upload from file' or QB Desktop import",
    ],
    sample: "quickbooks",
    columns: ["Date", "Description", "Amount"],
    faqs: [
      { q: "QB Online or Desktop?", a: "Both. The 3-column CSV is universal." },
    ],
    seoTitle: "PerCen360 for QuickBooks — SME grant compliance",
    seoDesc: "3-column bank CSV from PerCen360 to QuickBooks Online or Desktop. Irish grant tracking, GDPR-native.",
  },
  unit4: {
    name: "Unit4",
    products: "Unit4 Business World · Agresso platform",
    color: "#E4002B",
    audience: "Public sector, universities, local authorities",
    tagline: "Public sector-native CODA-friendly import.",
    hero: "The compliance backbone for Unit4-run Irish public sector bodies.",
    bullets: [
      "Unit4 CSV: TransDate, AccountCode, CostCentre, Description, Amount, Currency, TaxCode",
      "GRANT01 default cost-centre, easily configurable",
      "Common across Irish local authorities and third-level institutions",
    ],
    sample: "unit4",
    columns: ["TransDate", "AccountCode", "CostCentre", "Description", "Amount", "Currency", "TaxCode", "Reference"],
    faqs: [
      { q: "Does this support the Agresso variant?", a: "Yes — see the Agresso/CGI page for the local-authority-specific format." },
    ],
    seoTitle: "PerCen360 for Unit4 Business World — public sector",
    seoDesc: "Unit4 / Agresso transaction import CSV from PerCen360. Irish public sector and university compliance.",
  },
  agresso: {
    name: "Agresso / CGI",
    products: "Agresso Business World · CGI-supported deployments",
    color: "#0057B8",
    audience: "Local authorities, universities, semi-state bodies",
    tagline: "Local-authority-shaped voucher import.",
    hero: "The compliance feed for Agresso-run Irish local authorities.",
    bullets: [
      "Agresso CSV: Voucher_Date, GL_Account, Cost_Centre, Project, Description, Amount_EUR, VAT_Code",
      "IE23 / IE00 Revenue.ie VAT codes",
      "PERCEN360 project code — easily remapped to your local authority's convention",
    ],
    sample: "agresso",
    columns: ["Voucher_Date", "GL_Account", "Cost_Centre", "Project", "Description", "Amount_EUR", "VAT_Code", "Reference"],
    faqs: [
      { q: "Is this the format CGI use?", a: "Yes — the column names match the CGI-supported Agresso Business World voucher import used by Irish local authorities." },
    ],
    seoTitle: "PerCen360 for Agresso / CGI — Irish local authorities",
    seoDesc: "Agresso Business World voucher CSV from PerCen360 with Revenue.ie VAT codes. Irish local authority and semi-state ready.",
  },
};

const VENDOR_KEYS = Object.keys(VENDORS);

export default function IntegrationVendor() {
  const { vendor } = useParams();
  const v = VENDORS[vendor];
  const relatedVendors = useMemo(
    () => VENDOR_KEYS.filter(k => k !== vendor),
    [vendor]
  );

  useEffect(() => {
    if (!v) return;
    document.title = v.seoTitle;
    let meta = document.querySelector('meta[name="description"]');
    if (!meta) {
      meta = document.createElement("meta");
      meta.setAttribute("name", "description");
      document.head.appendChild(meta);
    }
    meta.setAttribute("content", v.seoDesc);
    // OG tags for LinkedIn / Slack previews
    const setOg = (prop, content) => {
      let el = document.querySelector(`meta[property="${prop}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute("property", prop);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };
    setOg("og:title", v.seoTitle);
    setOg("og:description", v.seoDesc);
    setOg("og:type", "website");
    // JSON-LD SoftwareApplication schema for rich snippets
    let ld = document.getElementById("percen360-ld-json");
    if (!ld) {
      ld = document.createElement("script");
      ld.type = "application/ld+json";
      ld.id = "percen360-ld-json";
      document.head.appendChild(ld);
    }
    ld.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "SoftwareApplication",
      "name": `PerCen360 for ${v.name}`,
      "applicationCategory": "BusinessApplication",
      "operatingSystem": "Web",
      "description": v.seoDesc,
      "offers": { "@type": "Offer", "price": "0", "priceCurrency": "EUR" },
    });

    return () => { document.title = "PerCen360 – Compliance Finance OS"; };
  }, [v]);

  if (!v) return <Navigate to="/compatibility" replace/>;

  const sampleUrl = `${API}/public/exports/sample/${v.sample}.csv`;

  return (
    <div className="min-h-screen aurora grain">
      <header className="hairline-b">
        <div className="max-w-[1200px] mx-auto px-6 sm:px-10 py-5 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl flex items-center justify-center gold-glow" style={{ background: "#FFB020" }}>
              <ShieldCheck size={22} weight="duotone" color="#050B0A" />
            </div>
            <div>
              <div className="font-display text-lg leading-none">PerCen360</div>
              <div className="text-[10px] tracking-[0.22em] uppercase text-white/50 mt-1">for {v.name}</div>
            </div>
          </Link>
          <div className="flex items-center gap-3">
            <Link to="/compatibility" className="btn-ghost text-sm hidden sm:inline-flex"><ArrowLeft size={13}/> All platforms</Link>
            <Link to="/" className="btn-gold text-sm">Try free <ArrowRight size={14} weight="bold"/></Link>
          </div>
        </div>
      </header>

      {/* HERO */}
      <section className="max-w-[1200px] mx-auto px-6 sm:px-10 pt-20 pb-16">
        <motion.div initial={{opacity:0,y:20}} animate={{opacity:1,y:0}} transition={{duration:0.6}} className="grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <div className="eyebrow mb-4 flex items-center gap-3">
              <span className="w-8 h-px" style={{ background: v.color }}/>
              <span style={{ color: v.color }}>{v.products}</span>
            </div>
            <h1 className="font-display text-5xl sm:text-6xl leading-[1.05] tracking-tight" data-testid="vendor-hero">
              {v.hero.split(v.name).map((chunk, i, arr) => (
                <span key={`${v.name}-part-${i}`}>
                  {chunk}
                  {i < arr.length - 1 && <span style={{ color: v.color }}>{v.name}</span>}
                </span>
              ))}
            </h1>
            <p className="mt-6 text-lg text-white/70 leading-relaxed max-w-2xl">
              {v.tagline} <span className="text-white/50">Serving: {v.audience}.</span>
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <a href={sampleUrl} className="btn-gold" data-testid={`vendor-sample-${vendor}`}>
                <FileArrowDown size={16} weight="fill"/> Download sample CSV
              </a>
              <a
                href={`${API}/public/one-pager/${vendor}.pdf`}
                target="_blank" rel="noreferrer"
                className="btn-ghost"
                data-testid={`vendor-pdf-${vendor}`}
              >
                <FileArrowDown size={14}/> PDF one-pager
              </a>
              <Link to="/" className="btn-ghost">
                <Lock size={14}/> Sign in to export live data
              </Link>
            </div>
          </div>
          <div className="lg:col-span-5">
            <div className="relative">
              <div className="absolute -inset-8 rounded-3xl blur-3xl opacity-40" style={{ background: `radial-gradient(ellipse at center, ${v.color}55, transparent 70%)` }}/>
              <div className="relative glass rounded-2xl p-8">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-14 h-14 rounded-xl flex items-center justify-center font-display text-2xl font-bold"
                    style={{ background: `${v.color}18`, border: `1px solid ${v.color}66`, color: v.color }}>
                    {v.name[0]}
                  </div>
                  <div>
                    <div className="font-display text-xl">{v.name}</div>
                    <div className="text-[10px] uppercase tracking-widest text-white/50 mt-1">{v.products}</div>
                  </div>
                </div>
                <div className="eyebrow mb-3">Column shape</div>
                <div className="mono text-[11px] text-white/70 space-y-1">
                  {v.columns.map(c => <div key={c} className="rounded bg-black/40 border border-white/5 px-3 py-1.5">{c}</div>)}
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </section>

      {/* BULLETS */}
      <section className="max-w-[1200px] mx-auto px-6 sm:px-10 py-16 border-t border-white/5">
        <div className="eyebrow mb-6">What you get</div>
        <div className="grid md:grid-cols-2 gap-4">
          {v.bullets.map((b, i) => (
            <motion.div key={b}
              initial={{opacity:0, y:12}} whileInView={{opacity:1, y:0}} viewport={{once:true}} transition={{duration:0.5, delay:i*0.05}}
              className="glass rounded-xl p-5 flex gap-4">
              <CheckCircle size={22} weight="fill" color="#00A86B" className="shrink-0 mt-0.5"/>
              <div className="text-white/80 text-sm leading-relaxed">{b}</div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="max-w-[1200px] mx-auto px-6 sm:px-10 py-16 border-t border-white/5">
        <div className="eyebrow mb-6">How it works</div>
        <div className="grid md:grid-cols-3 gap-5">
          {[
            { n: "01", t: "Capture in PerCen360", d: "Snap receipts, log grant expenses, import bank CSVs. AI runs Revenue.ie rule checks live." },
            { n: "02", t: `Export to ${v.name}`, d: `One click generates a column-matched CSV for ${v.products}. Optionally trigger a webhook.` },
            { n: "03", t: "Import & reconcile", d: `Drop the CSV into ${v.name} — accounts, VAT codes and references are pre-populated.` },
          ].map((s,i) => (
            <div key={s.n} className="glass rounded-xl p-6" data-testid={`step-${i}`}>
              <div className="text-4xl font-display" style={{ color: v.color }}>{s.n}</div>
              <div className="font-display text-lg mt-4">{s.t}</div>
              <div className="text-white/60 text-sm mt-2 leading-relaxed">{s.d}</div>
            </div>
          ))}
        </div>
      </section>

      {/* COMPLIANCE PROOF */}
      <section className="max-w-[1200px] mx-auto px-6 sm:px-10 py-16 border-t border-white/5">
        <div className="glass rounded-2xl p-8 sm:p-10 grid md:grid-cols-4 gap-6 items-center">
          <div className="md:col-span-3">
            <div className="eyebrow mb-3">Why it wins with grant reviewers</div>
            <h2 className="font-display text-3xl leading-tight">Every export is audit-hashed, GDPR-logged and Revenue.ie rule-checked.</h2>
            <p className="text-white/60 text-sm mt-3 max-w-2xl">
              PerCen360 doesn&apos;t just push data to {v.name} — it ships proof. Each row is tied back to a compliance certificate you can share with Enterprise Ireland, Horizon Europe or EIT Health reviewers.
            </p>
          </div>
          <div className="flex justify-center">
            <div className="w-32 h-32 rounded-full flex items-center justify-center gold-glow" style={{ background: "rgba(255,176,32,0.12)", border: "2px solid rgba(255,176,32,0.4)" }}>
              <ShieldCheck size={56} weight="duotone" color="#FFB020"/>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="max-w-[900px] mx-auto px-6 sm:px-10 py-16 border-t border-white/5">
        <div className="eyebrow mb-6">Common questions</div>
        <div className="space-y-3">
          {v.faqs.map(f => (
            <details key={f.q} className="glass rounded-xl p-5 group">
              <summary className="cursor-pointer font-display text-base list-none flex items-center justify-between gap-4">
                <span>{f.q}</span>
                <span className="text-[#FFB020] text-xl transition-transform group-open:rotate-45">+</span>
              </summary>
              <div className="text-white/65 text-sm mt-3 leading-relaxed">{f.a}</div>
            </details>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-[1200px] mx-auto px-6 sm:px-10 py-16 border-t border-white/5">
        <div className="glass rounded-2xl p-10 sm:p-14 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-8">
          <div>
            <div className="eyebrow mb-3">Ready when the audit is</div>
            <h2 className="font-display text-3xl sm:text-4xl max-w-2xl leading-tight">Start pushing grant compliance data into {v.name} today.</h2>
          </div>
          <div className="flex gap-3 flex-wrap">
            <a href={sampleUrl} className="btn-ghost"><FileArrowDown size={14}/> Sample CSV</a>
            <a href={`${API}/public/one-pager/${vendor}.pdf`} target="_blank" rel="noreferrer" className="btn-ghost">
              <FileArrowDown size={14}/> PDF one-pager
            </a>
            <Link to="/" className="btn-gold">Sign in with Google <ArrowRight size={16} weight="bold"/></Link>
          </div>
        </div>
      </section>

      {/* Related vendors */}
      <section className="max-w-[1200px] mx-auto px-6 sm:px-10 py-16 border-t border-white/5">
        <div className="eyebrow mb-6">Other supported platforms</div>
        <div className="flex flex-wrap gap-2">
          {relatedVendors.map(k => (
            <Link key={k} to={`/integrations/${k}`} className="btn-ghost text-xs" data-testid={`related-${k}`}>
              {VENDORS[k].name}
            </Link>
          ))}
          <Link to="/compatibility" className="btn-ghost text-xs">
            <TreeStructure size={12}/> All platforms →
          </Link>
        </div>
      </section>

      <footer className="border-t border-white/5 py-10">
        <div className="max-w-[1200px] mx-auto px-6 sm:px-10 flex flex-col md:flex-row justify-between items-start md:items-center gap-4 text-xs text-white/50">
          <div className="flex items-center gap-3">
            <ShieldCheck size={16} weight="duotone" color="#FFB020"/>
            © {new Date().getFullYear()} PerCen Technologies Ltd · percen360.com · EU region
          </div>
          <div className="flex gap-5">
            <Link to="/compatibility" className="hover:text-white">Compatibility</Link>
            <Link to="/privacy" className="hover:text-white">Privacy</Link>
            <Link to="/terms" className="hover:text-white">Terms</Link>
            <Link to="/dpia" className="hover:text-white">DPIA</Link>
            <a href="mailto:gillian@percen360.com" className="hover:text-white flex items-center gap-1"><LinkSimple size={11}/> Contact</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
